import React, { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import { analytics } from '../../services/analytics';

/** Patterns that indicate a connectivity / network failure rather than a code bug. */
const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',
  'network error',
  'networkerror',
  'net::err_',
  'econnrefused',
  'enotfound',
  'etimedout',
  'unable to connect',
  'load failed',
  'the internet connection appears to be offline',
] as const;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  level?: 'page' | 'section' | 'component';
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

interface ErrorBoundaryClassProps extends Props {
  t: (key: string) => string;
}

interface State {
  hasError: boolean;
  error?: Error | undefined;
  errorId?: string | undefined;
  prevResetKeys?: Array<string | number> | undefined;
}

class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, State> {
  constructor(props: ErrorBoundaryClassProps) {
    super(props);
    this.state = {
      hasError: false,
      prevResetKeys: props.resetKeys,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryClassProps,
    state: State
  ): Partial<State> | null {
    const { resetKeys, resetOnPropsChange } = props;
    const { prevResetKeys, hasError } = state;

    // Reset error state when reset keys change
    if (hasError && resetKeys && prevResetKeys) {
      if (resetKeys.some((key, index) => key !== prevResetKeys[index])) {
        return {
          hasError: false,
          error: undefined,
          errorId: undefined,
          prevResetKeys: resetKeys,
        };
      }
    }

    // Reset on any prop change if enabled
    if (hasError && resetOnPropsChange) {
      return {
        hasError: false,
        error: undefined,
        errorId: undefined,
      };
    }

    // Update reset keys
    if (resetKeys !== prevResetKeys) {
      return { prevResetKeys: resetKeys };
    }

    return null;
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorId = this.state.errorId || 'unknown';
    analytics.trackError(window.location.pathname || 'unknown', error.name, error.message);

    // Log to our logging service
    logger.error({
      message: 'React Error Boundary caught an error',
      error,
      context: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        level: this.props.level || 'component',
        errorId,
      },
    });

    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorId: undefined,
    });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error } = this.state;
      const level = this.props.level || 'component';

      // When the device is offline OR the caught error is network-related,
      // show the dedicated offline fallback instead of the generic error UI.
      const isNetworkCause =
        !navigator.onLine ||
        (error !== undefined &&
          NETWORK_ERROR_PATTERNS.some(pattern => error.message?.toLowerCase().includes(pattern)));

      if (isNetworkCause) {
        return (
          <div className="flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 bg-ds-bg rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-ds-text-tertiary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636a9 9 0 010 12.728M5.636 5.636a9 9 0 000 12.728M12 12h.01"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-ds-text-primary mb-2">
                {this.props.t('connectionLost')}
              </h3>
              <p className="text-sm text-ds-text-secondary mb-4">
                {this.props.t('checkConnectionAndRetry')}
              </p>
              <button
                onClick={this.handleRetry}
                className="px-6 py-2 bg-primary text-white rounded-ds-default hover:bg-primary/90 transition-colors"
              >
                {this.props.t('tryAgain')}
              </button>
            </div>
          </div>
        );
      }

      // Full page error
      if (level === 'page') {
        return (
          <div className="min-h-screen flex items-center justify-center bg-ds-bg">
            <div className="max-w-md mx-auto text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-error"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-ds-text-primary mb-2">
                {this.props.t('somethingWentWrong')}
              </h1>
              <p className="text-ds-text-secondary mb-4">
                {this.props.t('unexpectedErrorMessage')}
              </p>
              <div className="space-x-3">
                <button
                  onClick={this.handleRetry}
                  className="px-6 py-2 bg-primary text-white rounded-ds-default hover:bg-primary-700 transition-colors"
                >
                  {this.props.t('tryAgain')}
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 border border-primary text-primary rounded-ds-default hover:bg-primary-50 transition-colors"
                >
                  {this.props.t('refreshPage')}
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Section or component level error
      return (
        <div className="flex items-center justify-center p-6 bg-error-container rounded-ds-default border border-error/20">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-error/10 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-error"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-error-on-container mb-2">
              {this.props.t('somethingWentWrong')}
            </h3>
            <p className="text-xs text-error-on-container/70 mb-3">
              {this.props.t('sectionErrorMessage')}
            </p>
            <button
              onClick={this.handleRetry}
              className="px-4 py-1.5 text-xs bg-error text-white rounded hover:bg-error/90 transition-colors"
            >
              {this.props.t('tryAgain')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper to provide translation function to class component
export const ErrorBoundary: React.FC<Props> = props => {
  const { t } = useTranslation();
  return <ErrorBoundaryClass {...props} t={t} />;
};

export default ErrorBoundary;
